import type {
  Representative,
  RepresentativeFormData,
  RepresentativeStatus
} from "../types/representative";
import { formatDisplayDate } from "../utils/displayDate";
import { formatPhoneForApiPayload } from "../utils/validation/legalEntityValidation";
import { apiRequest } from "./apiClient";

type BackendPaginatedResponse<T> = {
  readonly success?: boolean;
  readonly data: T[];
  readonly meta?: {
    readonly total?: number;
    readonly limit?: number;
    readonly offset?: number;
  };
  readonly message?: string;
};

type BackendSuccessResponse<T> = {
  readonly success?: boolean;
  readonly data: T;
  readonly message?: string;
};

type BackendRepresentativeItem = {
  readonly id: string;
  readonly entityName?: string | null;
  readonly name?: string | null;
  readonly title?: string | null;
  readonly company?: string | null;
  readonly streetAddress?: string | null;
  readonly city?: string | null;
  readonly state?: string | null;
  readonly zip?: string | null;
  readonly phone?: string | null;
  readonly email?: string | null;
  readonly address?: string | null;
  readonly status?: RepresentativeStatus | null;
  readonly createdOn?: string | null;
  readonly modifiedOn?: string | null;
  readonly createdBy?: string | null;
  readonly modifiedBy?: string | null;
};

export type RepresentativeSortBy =
  | "created_at"
  | "created_by"
  | "updated_by"
  | "updated_at"
  | "entity_name"
  | "name"
  | "title";

export type RepresentativeListParams = {
  readonly limit: number;
  readonly offset: number;
  readonly q?: string;
  readonly status?: RepresentativeStatus[];
  readonly updatedBy?: string[];
  readonly sort_by?: RepresentativeSortBy;
  readonly sort_dir?: "asc" | "desc";
};

export type RepresentativeListResult = {
  readonly items: Representative[];
  readonly total: number;
};

const toRepresentative = (item: BackendRepresentativeItem): Representative => {
  const entityName = (item.entityName ?? item.company ?? "").trim();
  const streetAddress = (item.streetAddress ?? "").trim();
  const city = (item.city ?? "").trim();
  const state = (item.state ?? "").trim();
  const zip = (item.zip ?? "").trim();
  const mergedAddress = [streetAddress, city, state, zip].filter(Boolean).join(", ");

  return {
    id: item.id,
    entityName: entityName || "Unknown",
    name: (item.name ?? "").trim(),
    title: (item.title ?? "").trim(),
    company: (item.company ?? entityName).trim(),
    streetAddress,
    city,
    state,
    zip,
    phone: (item.phone ?? "").trim(),
    email: (item.email ?? "").trim(),
    address: (item.address ?? mergedAddress).trim(),
    createdBy: item.createdBy ?? "Unknown",
    createdOn: item.createdOn
      ? formatDisplayDate(item.createdOn)
      : formatDisplayDate(new Date()),
    modifiedBy: item.modifiedBy ?? "Unknown",
    modifiedOn: item.modifiedOn ? formatDisplayDate(item.modifiedOn) : undefined,
    status: item.status ?? "Inactive"
  };
};

export const listRepresentatives = async (
  params: RepresentativeListParams
): Promise<RepresentativeListResult> => {
  const q = new URLSearchParams();
  q.set("limit", String(params.limit));
  q.set("offset", String(params.offset));
  if (params.q?.trim()) {
    q.set("q", params.q.trim());
  }
  for (const s of params.status ?? []) {
    q.append("status", s);
  }
  for (const u of params.updatedBy ?? []) {
    q.append("updated_by", u);
  }
  if (params.sort_by) {
    q.set("sort_by", params.sort_by);
  }
  if (params.sort_dir) {
    q.set("sort_dir", params.sort_dir);
  }

  const response = await apiRequest(`/representatives?${q.toString()}`);
  const json = (await response.json()) as BackendPaginatedResponse<BackendRepresentativeItem>;
  const items = Array.isArray(json.data) ? json.data.map(toRepresentative) : [];
  const total = typeof json.meta?.total === "number" ? json.meta.total : items.length;
  return { items, total };
};

export const createRepresentative = async (
  formData: RepresentativeFormData
): Promise<Representative> => {
  const response = await apiRequest("/representatives", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      name: formData.name,
      title: formData.title,
      company: formData.company,
      streetAddress: formData.streetAddress,
      city: formData.city,
      state: formData.state,
      zip: formData.zip,
      phone: formatPhoneForApiPayload(formData.phone),
      email: formData.email
    })
  });

  const json = (await response.json()) as BackendSuccessResponse<BackendRepresentativeItem>;
  return toRepresentative(json.data);
};

export const updateRepresentativeStatus = async (
  id: string,
  status: RepresentativeStatus
): Promise<RepresentativeStatus> => {
  const response = await apiRequest(`/representatives/${id}/status`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ status })
  });

  const json = (await response.json()) as BackendSuccessResponse<BackendRepresentativeItem>;
  return json.data.status ?? status;
};
